import React from "react";
import { connect } from "react-redux";
import { FormSection, Field, FieldArray, formValueSelector } from "redux-form";
import * as normalizers from "common/normalizers";
import * as validations from "common/validations";
import Modal from "components/Modal";
import FieldGroup from "components/FieldGroup";
import InputPack from "components/InputPack";
import RadioGroupPack from "components/RadioGroupPack";
import * as animalUtils from "../_utils";
import HealthSupplementsTotals from "./HealthSupplementsTotals";
import HealthSupplementsDrenching from "./HealthSupplementsDrenching";
import SaltBlocksFieldArray from "./SaltBlocksFieldArray";

const OPTIONS = [
    { value: "Totals", text: "Total annual amount applied" },
    { value: "Drenching", text: "Drenching and dusting regimes" },
];

class HealthSupplementsModal extends React.Component {
    constructor(props) {
        super(props);
        const healthSupplements = props.initialValues;

        let supplementsType = OPTIONS[0].value;
        if (!healthSupplements.totals && healthSupplements.drenching) supplementsType = OPTIONS[1].value;

        let state = {
            supplementsType: supplementsType,
        };

        if (supplementsType === OPTIONS[1].value) {
            state = {
                ...state,
                disableMagnesiumFields: !healthSupplements.drenching.mgMaterial || healthSupplements.drenching.mgMaterial === "None",
                disableSaltFields: !healthSupplements.drenching.saltRate_gCowDay && !healthSupplements.drenching.saltNumberOfWeeksUsed,
                disableLimeFlourFields: !healthSupplements.drenching.limeFlourRate_gCowDay && !healthSupplements.drenching.limeFlourNumberOfWeeksUsed,
            };
        }
        this.state = state;
    }

    // Use redux form validate function to validate age or weight provided for bring-on events
    _validate = (healthSupplements) => {
        const errors = {};

        if (healthSupplements.supplementsType === "Drenching") errors.drenching = this._validateDrenching(healthSupplements.drenching);

        return errors;
    };

    _validateDrenching = (drenching) => {
        const errors = {};

        if (!drenching) return errors;

        if (drenching.mgMaterial && drenching.mgMaterial !== "None") {
            errors.mgRate_gCowDay = validations.requiredInt(drenching.mgRate_gCowDay);
            errors.mgNumberOfWeeksUsed = validations.requiredInt(drenching.mgNumberOfWeeksUsed);
        } else {
            drenching.mgRate_gCowDay = null;
            drenching.mgNumberOfWeeksUsed = null;
        }

        if (drenching.saltMaterial === true) {
            errors.saltRate_gCowDay = validations.requiredInt(drenching.saltRate_gCowDay);
            errors.saltNumberOfWeeksUsed = validations.requiredInt(drenching.saltNumberOfWeeksUsed);
        } else {
            drenching.saltRate_gCowDay = null;
            drenching.saltNumberOfWeeksUsed = null;
        }

        if (drenching.limeFlourMaterial === true) {
            errors.limeFlourRate_gCowDay = validations.requiredInt(drenching.limeFlourRate_gCowDay);
            errors.limeFlourNumberOfWeeksUsed = validations.requiredInt(drenching.limeFlourNumberOfWeeksUsed);
        } else {
            drenching.limeFlourRate_gCowDay = null;
            drenching.limeFlourNumberOfWeeksUsed = null;
        }

        return errors;
    };

    _supplementsTypeChanged = (e) => {
        this.setState({
            supplementsType: e.target.value,
        });
    };

    render() {
        const { initialValues: healthSupplements, modal, refData, formState } = this.props;

        const _onSubmit = (healthSupplements) => {
            if (healthSupplements.supplementsType === "Totals") {
                delete healthSupplements.drenching;
            } else if (healthSupplements.supplementsType === "Drenching") {
                delete healthSupplements.totals;
                delete healthSupplements.nitrogen_kgYr;
                delete healthSupplements.phosphate_kgYr;
            }
            modal.onConfirm(healthSupplements);
        };

        const saltBlockTypes = animalUtils.getSaltBlockTypeOptions(refData);

        const showTotals = this.state.supplementsType === OPTIONS[0].value;
        const showDrenching = this.state.supplementsType === OPTIONS[1].value;

        return (
            <Modal title="Health supplements" submitLabel="Done" initialValues={healthSupplements} onSubmit={_onSubmit} validate={this._validate}>
                <div className="Grid">
                    <div className="Grid-cell u-lg-width2of3">
                        <h3>Magnesium, salt and lime flour</h3>
                        {healthSupplements.enterpriseType !== "Dairy" && <p>Enter the total amount applied per year when drenching, pasture dusting or adding to supplements</p>}
                        {healthSupplements.enterpriseType === "Dairy" && (
                            <div>
                                <p>Enter the total amount per year or describe the dusting and drenching regime</p>
                                <Field name="supplementsType" options={OPTIONS} inline={true} onChange={this._supplementsTypeChanged} validate={[validations.required]} component={RadioGroupPack} />
                            </div>
                        )}
                    </div>
                    {showTotals && (
                        <FormSection name="totals">
                            <HealthSupplementsTotals />
                        </FormSection>
                    )}
                    {showDrenching && (
                        <FormSection name="drenching">
                            <HealthSupplementsDrenching healthSupplements={healthSupplements} formState={formState} magnesiumSources={refData.magnesiumSources} />
                        </FormSection>
                    )}
                    {showTotals && (
                        <div className="Grid-cell u-lg-width2of3">
                            <h3>Added to feed</h3>
                            <FieldGroup>
                                <Field name="nitrogen_kgYr" label="Urea (N)" placeholder="0" type="text" uom="kg/yr" dataWidth="33" normalize={normalizers.integer} validate={[validations.range(0, 99999)]} component={InputPack} />
                                <Field name="phosphate_kgYr" label="Phosphate (P)" placeholder="0" type="text" uom="kg/yr" dataWidth="33" normalize={normalizers.integer} validate={[validations.range(0, 99999)]} component={InputPack} />
                            </FieldGroup>
                        </div>
                    )}
                    <div className="Grid-cell u-mt-lg">
                        <FieldArray name="saltBlocks" saltBlockTypes={saltBlockTypes} component={SaltBlocksFieldArray} />
                    </div>
                </div>
            </Modal>
        );
    }
}

const _mapStateToProps = (state) => {
    const selector = formValueSelector("modal-form");
    return {
        formState: selector(state, "drenching"),
        refData: state.app.refData,
    };
};

export default connect(_mapStateToProps, null)(HealthSupplementsModal);
